import { createContext, ReactNode, useContext, useState } from 'react';
import { ToastNotificationProps, ToastPositions } from '../../molecules/Toast';

interface ProviderProps {
  children: ReactNode;
}

export type ToastNotificationData = Partial<Omit<ToastNotificationProps, 'close' | 'index'>> & {
  hideTimeout?: null | number;
  position?: ToastPositions;
  id?: number;
  parentElement?: HTMLElement;
  /**
   * Called when the toast is closed (close button or hideTimeout). Optional.
   */
  onClose?: () => void;
};

interface ToastNotificationProviderData {
  toastNotificationState: ToastNotificationData[];
  showToastNotification: (payload: ToastNotificationData) => void;
  closeToastNotification: (index?: number) => void;
}

export const ToastNotificationContext = createContext({} as ToastNotificationProviderData);

let nextToastId = 0;

const generateToastId = () => {
  nextToastId += 1;

  return nextToastId;
};

export const ToastNotificationProvider = ({ children }: ProviderProps) => {
  const [toastNotificationState, setToastNotificationState] = useState<ToastNotificationData[]>([]);

  const showToastNotification = (payload: ToastNotificationData) => {
    setToastNotificationState((state) => [
      ...state,
      {
        ...payload,
        hideTimeout: payload.type === 'success' ? 10 : payload.hideTimeout,
        id: generateToastId(),
        position: payload.position || 'top',
      },
    ]);
  };

  const closeToastNotification = (id?: number) => {
    setToastNotificationState((state) => {
      const newToastNotificationState = [...state];
      const index = newToastNotificationState.findIndex((toast) => toast.id === id);

      newToastNotificationState.splice(index, 1);

      return newToastNotificationState;
    });
  };

  return (
    <ToastNotificationContext.Provider
      value={{
        toastNotificationState,
        showToastNotification,
        closeToastNotification,
      }}
    >
      {children}
    </ToastNotificationContext.Provider>
  );
};

export const useToastNotificationContext = (): ToastNotificationProviderData => {
  const context = useContext(ToastNotificationContext);

  if (!context) {
    throw new Error('useToastNotificationContext must be used within a ToastNotificationProvider');
  }

  return context;
};
